import { Branding, BrandingDisabledIf, DisabledPrompt, useModel } from "bcms-react";
import Util from "classes/Util";
import { SVGAsset } from 'res';
import { MobileNavItem, MobileNavMenuItem, MobileNavDropdownMenuItem, MobileNavSubDropdownMenuItem } from "../..";

/**
 * @function BID1225BMobileNavItems
 * @extends React.Component
 * @exported true
 * @description The main component for navigation for BID 1225B in mobile.
 * @example
   <BID1225BMobileNavItems links={{
    profile: profileItem,
    settings: settingsItem
   }}/>
 */
export default function BID1225BMobileNavItems(props) {
  const options = useModel().branding.options;

  const chatLink = () => {
    if (Util.inMabi) {
      Util.openMabiChat()
    }
    else {
      window.open(window.location.href.slice(0, window.location.href.indexOf("#")) + "#/chat", "_blank")
    }

  };

  const hasMultiProfileOptionOn = options.multiprofile_abi22 === "true";
  let changeProfilePath = `/changeProfile`;

  const newAccountsItem = <MobileNavDropdownMenuItem className="accounts-mobile-link" itemIcon={<SVGAsset id="WalletIcon" />} label="Accounts" dataPoint="Nav_Accounts">
    <MobileNavMenuItem className="account-summary-mobile-link" to="/accountSummary" label="Account Summary" dataPoint="Nav_Account_Summary" />
    <MobileNavMenuItem className="statements-mobile-link" to="/statements" label="Statements & Notices" dataPoint="Nav_eStatements_Noticies" />
    <MobileNavMenuItem className="stop-payments-mobile-link" to="/stopPayment" label="Stop Payments" dataPoint="Nav_Stop_Payments" />
    { !Util.inMabi && 
      <MobileNavMenuItem className="web-connect-link" label="WebConnect" to="/download" dataPoint="Nav_Download" />
    }
    <DisabledPrompt>
      <MobileNavMenuItem className="fraud-prevention-link" label="Fraud Prevention" dataPoint="Nav_Fraud_Prevention" />
    </DisabledPrompt>
  </MobileNavDropdownMenuItem>;

  const transfersItem = <MobileNavDropdownMenuItem className="transfer-mobile-link" itemIcon={<SVGAsset id="TransfersIcon" />} label="Transfers" dataPoint="Nav_Transfers">
    <MobileNavMenuItem className="make-a-transfer-mobile-link" to="/transfers" label="Make a Transfer" dataPoint="Nav_Make_Transfer" />
    <MobileNavMenuItem className="external-transfers-mobile-link" label="External Transfers" to="/externalTransfers" dataPoint="Nav_External_Transfers" />
    <MobileNavMenuItem className="transfer-activity-mobile-link" to="/transferActivity/scheduledTransfers" label="Transfer Activity" dataPoint="Nav_Transfer_Activity" />
    <MobileNavMenuItem className="transfer-accounts-mobile-link" to="/unlinkedTransferAccounts" label="Intrabank Accounts" dataPoint="Nav_Transfer_Accounts" />
  </MobileNavDropdownMenuItem>;

  const paymentsItem = <DisabledPrompt><MobileNavItem className="payments-mobile-link" itemIcon={<SVGAsset id="DollarSignOutlineIcon" />} label="Payments" dataPoint="Nav_Payments" /></DisabledPrompt>

  const cardItem = <DisabledPrompt>
    <MobileNavItem className="ch21-mobile-link" itemIcon={<SVGAsset id="CardIcon" />} label="Cards" to="/ch21" dataPoint="Nav_Cards" />
  </DisabledPrompt>

  const zelleMobileNavItem = <MobileNavItem className="zle-mobile-link" itemIcon={<SVGAsset id="ZelleIcon" />} to={hasMultiProfileOptionOn ? changeProfilePath : "/zelle"} label={<>Zelle<span className="r-mark">&reg;</span></>} dataPoint="Nav_Zelle" />
  
  const depositsItem = <>
    {Util.inMabi &&
      <>
        <MobileNavDropdownMenuItem className="deposits-mobile-link" itemIcon={<SVGAsset id="CameraIcon" />} label="Deposits" dataPoint="Nav_Deposits">
          <MobileNavMenuItem className="deposit-a-check-link" to="/depositACheckWorkflow" label="Deposit a Check" dataPoint="Nav_Deposit_A_Check" />
          <MobileNavMenuItem className="deposit-history-link" to="/depositHistory" label="Deposit History" dataPoint="Nav_Deposit_History" />
        </MobileNavDropdownMenuItem>
      </>
    }
  </>

  const achItem = <MobileNavDropdownMenuItem className="ach-mobile-link" itemIcon={<SVGAsset id="BankIcon" />} label="ACH" dataPoint="Nav_ACH">
    <MobileNavMenuItem className="ach-participants-mobile-link" to="/achParticipants" label="ACH Participants" dataPoint="Nav_ACH_Participants" />
    <MobileNavMenuItem className="one-time-ach-mobile-link" to="/oneTimeACHWorkflow" label="One-Time ACH Payment" dataPoint="Nav_One-Time_ACH_Payment" />
    <MobileNavMenuItem className="ach-templates-mobile-link" to="/achTemplates" label="ACH Templates" dataPoint="Nav_ACH_Templates" />
    <MobileNavMenuItem className="ach-activity-mobile-link" to="/achActivity" label="ACH Activity" dataPoint="Nav_ACH_Activity" />
    <DisabledPrompt>
      <MobileNavMenuItem className="nacha-import-mobile-link" label="NACHA Import" dataPoint="Nav_NACHA_Import" />
    </DisabledPrompt>
    <MobileNavSubDropdownMenuItem label="Import File" dataPoint="Nav_Import_File">
      <MobileNavMenuItem className="participant-import-mobile-link" to="/achImport" label="Participant Import" dataPoint="Nav_Import" />
      <DisabledPrompt>
        <MobileNavMenuItem className="non-nacha-import-mobile-link" label="Non-NACHA Import" dataPoint="Nav_Non_NACHA_Import" />
      </DisabledPrompt>
    </MobileNavSubDropdownMenuItem>
    <MobileNavMenuItem className="state-tax-mobile-link" to="/stateTaxIDPage" label="State Tax IDs" dataPoint="Nav_State_Tax_IDs" />
    <MobileNavMenuItem className="deny-users-mobile-link" to="/denyUsers" label="Deny Users" dataPoint="Nav_Deny_Users" />
  </MobileNavDropdownMenuItem>;

  const wiresItem = <MobileNavDropdownMenuItem className="wires-mobile-link" itemIcon={<SVGAsset id="WiresIcon" />} label="Wires" dataPoint="Nav_Wires">
    <DisabledPrompt>
      <MobileNavMenuItem className="wire-payees-mobile-link" label="Wire Payees" dataPoint="Nav_Wire_Payees" />
    </DisabledPrompt>
    <DisabledPrompt>
      <MobileNavMenuItem className="single-wire-mobile-link" label="Single Wire" dataPoint="Nav_Single_Wire" />
    </DisabledPrompt>
    <DisabledPrompt>
      <MobileNavMenuItem className="wire-activity-mobile-link" label="Wire Activity" dataPoint="Nav_Wire_Activity" />
    </DisabledPrompt>
  </MobileNavDropdownMenuItem>

  const profileItem = <MobileNavDropdownMenuItem className="profile-mobile-link" itemIcon={<SVGAsset id="CircleProfileIcon" />} label="Profile" dataPoint="Utility_Profile">
    <MobileNavMenuItem className="contact-info-mobile-link" to="/actContactInfo" label="Contact Information" dataPoint="Utility_Contact_Information" />
    <MobileNavMenuItem className="password-mobile-link" to="/password" label="Password" dataPoint="Utility_Password" />
    <MobileNavMenuItem className="stopped-numbers-mobile-link" to="/stoppedNumbers" label="Stopped Numbers" dataPoint="Utility_Stopped_Numbers" />
    <MobileNavMenuItem className="statement-preference-mobile-link" label="Statement Preference" to="/statementPreference" dataPoint="Nav_ePreference" />
    <DisabledPrompt>
      <MobileNavMenuItem className="tax-documents-mobile-link" label="Tax Documents" route="/taxDocuments" dataPoint="Utility_Tax_Documents" />
    </DisabledPrompt>
  </MobileNavDropdownMenuItem>;


  const settingsItem = <MobileNavDropdownMenuItem className="settings-mobile-link" itemIcon={<SVGAsset id="GearIcon" />} label="Settings" dataPoint="Utility_Settings">
    { Util.inMabi && 
      <MobileNavMenuItem className="authorized-device-enrollment-mobile-link" to="/authorizedDeviceEnrollmentWorkflow" label="Authorized Device Enrollment" dataPoint="Utility_Authorized_Device_Enrollment" /> 
    }
    <MobileNavMenuItem className="alerts-mobile-link" to="/notifications" label="Alerts" dataPoint="Utility_Alerts" />
    <MobileNavMenuItem className="authorized-devices-mobile-link" to="/authorizedDevicesWorkflow" label="Authorized Devices" dataPoint="Utility_Authorized_Devices" />
    <MobileNavMenuItem className="fast-balances-mobile-link" to="/settingsFastBalancesWorkflow" label="Fast Balances" dataPoint="Utility_Instance_Balances" />
    <MobileNavMenuItem className="authorized-apps-mobile-link" to="/authorizedApps" label="Authorized Apps" dataPoint="Utility_Authorized_Apps" />
    { !Util.inMabi && 
      <MobileNavMenuItem className="users-mobile-link" to="/addUserWorkflow" label="Users" dataPoint="Utility_Users" />
    }
    <MobileNavMenuItem className="accepted-disclosures-mobile-link" to="/acceptedDisclosuresWorkflow" label="Accepted Disclosures" dataPoint="Utility_Accepted_Disclosures" />
    <DisabledPrompt>
      <MobileNavMenuItem className="delete-an-account-mobile-link" to="/deleteanaccount" label="Delete an Account" dataPoint="Utility_Delete_Account" />
    </DisabledPrompt>
  </MobileNavDropdownMenuItem>;

  const utilityNavItems =
    <>
      <MobileNavItem className="messages-mobile-link" to="/messages" label="Messages" itemIcon={<SVGAsset id="EnvelopeIcon" />} dataPoint="Nav_Messages">
        <div className="set-width alert-bubble flex-container all-center">
          <span>3</span>
        </div>
      </MobileNavItem>
      <MobileNavItem className="chat-mobile-link" onClick={chatLink} label="Chat" itemIcon={<SVGAsset id="ChatIcon" />} dataPoint="Nav_Chat" />
    </>

  return (
    <Branding id="bid_abi22" value="1225B" >
      {newAccountsItem}
      {transfersItem}
      {paymentsItem}
      {cardItem}
      {zelleMobileNavItem}
      {depositsItem}
      {achItem}
      {wiresItem}
      {profileItem}
      {settingsItem}
      {utilityNavItems}
    </Branding>
  );
}

